import type { UseQueryResult } from '@tanstack/react-query'
import { ReactNode } from 'react'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { LOADING } from '../components/loading'
import { ListContextProvider } from './list-context'
import { Pagination } from './pagination'
import type { ParamsType, SetParamsType } from './types'

const DEFAULT_PARAMS: ParamsType = {
	page: 1,
	orderKey: 'id',
	orderDir: 'DESC',
}

export function useList(defaultParams: ParamsType = DEFAULT_PARAMS): [ParamsType, SetParamsType] {
	const navigate = useNavigate()
	const location = useLocation() as Location<ParamsType | undefined>
	const params = location.state ?? defaultParams
	const setParams: SetParamsType = newState => {
		const state = typeof newState === 'function' ? newState(params) : { ...params, ...newState }
		navigate(location.pathname, { replace: true, state })
	}

	return [params, setParams]
}

type ListProps<T> = {
	children: (entity: T) => ReactNode
	id?: string
	header: ReactNode
	footer?: ReactNode
	params: ParamsType
	query: UseQueryResult<{
		list: T[]
		total: number
	}>
	setParams: SetParamsType
}

export function List<T>({ id, header, footer, params, setParams, query, children }: ListProps<T>) {
	if (query.isLoading) {
		return (
			<div className="flex items-center p-6">
				{LOADING}
				Načítám…
			</div>
		)
	}
	if (!query.isSuccess) {
		return null
	}
	const data = query.data?.list
	const total = query.data?.total
	if (!data.length) {
		return <p className="px-3 py-3 sm:px-5 sm:py-4 text-gray-400">Nebyla nalezena žádná data</p>
	}
	return (
		<ListContextProvider params={params} setParams={setParams}>
			<div className="relative">
				{!data.length && query.isFetching ? (
					<div className="flex items-center p-6">
						{LOADING}
						Načítám…
					</div>
				) : null}
				<table id={id} className="w-full">
					<thead>{header}</thead>
					<tbody>{data.map(entity => children(entity))}</tbody>
					{footer && <tfoot>{footer}</tfoot>}
				</table>
				<Pagination params={params} setParams={setParams} total={total} />
			</div>
		</ListContextProvider>
	)
}
